import { useCallback, useEffect, useRef, useState } from 'react';
import type { BarChart, PieChart, ChartData } from '@trinity/chat';
import { CurrentSegmentIdsContext } from '@trinity/chat-components/containers/Segmentation.Dashboard/context';
import { useCookie } from '@trinity/chat-components/hooks/useCookie';
import { LegendCollapseToggleContext, ChartInfoContext, ChartGroupByContext, ChartTypeContext, ChartViewContext, ChartRecordIdContext } from '../Chat/context';
import { mapChartTypeToBackend } from './Chat.Chart.utils';
import { ChartThemeIdContext } from './context';
import { useCreateChartRecordMutation } from './hooks/useCreateChartPreferenceMutation';

type Props = {
  chart: ChartData;
  children: React.ReactNode;
};

// converting bar chart data to a pie chart data
export const barToPie = (data: BarChart): PieChart => {
  const pieData: PieChart['data'] = [];

  for (let i = 0; i < data.data.length; i++) {
    pieData.push({
      id: data.data[i].x,
      label: data.data[i].x,
      value: data.data[i].y,
    });
  }

  return {
    title: data.title,
    xAxisTitle: data.xAxisTitle,
    yAxisTitle: data.yAxisTitle,
    data: pieData,
  };
};

export const pieToBar = (data: PieChart): BarChart => {
  const barData: BarChart['data'] = [];

  for (let i = 0; i < data.data.length; i++) {
    barData.push({
      x: data.data[i].label,
      y: data.data[i].value,
    });
  }

  return {
    indexBy: data.xAxisTitle,
    title: data.title,
    xAxisTitle: data.xAxisTitle,
    yAxisTitle: data.yAxisTitle,
    data: barData,
  };
};

export const ChartLegendContainer = (props: {
  children: React.ReactNode;
  defaultValue?: boolean;
}) => {
  const [open, setOpen] = useState(props.defaultValue);
  return (
    <LegendCollapseToggleContext.Provider value={[open, setOpen]}>
      {props.children}
    </LegendCollapseToggleContext.Provider>
  );
};

const parseThemeCookieValue = (cookie: string): number | null => {
  if (!cookie) return null;
  const parsed = parseInt(cookie, 10);
  return Number.isNaN(parsed) ? null : parsed;
};


export const ChartContainer = (props: Props) => {

  const [chart, setChart] = useState<ChartData>(props.chart);
  const [groupBy, setGroupBy] = useState<number | null>(null);
  const [chartType, setChartType] = useState<string | null>(props.chart?.chart?.type);
  const chartRecordIdRef = useRef<number | null>(null);
  const chartRecordedRef = useRef<boolean>(false);
  const ChartView = useState<'chart' | 'table'>('chart');

  const themeCookieName = `chat-chart:selected-theme:project-${props.chart?.projectId ?? ''}`;
  const [themeCookie] = useCookie(themeCookieName, undefined, { watchForChanges: true });

  // Theme customization state initialize from cookie
  const [selectedThemeId, setSelectedThemeId] = useState<number | null>(
    parseThemeCookieValue(themeCookie),
  );

  // Keep selectedThemeId in sync with the cookie value, in case it is updated elsewhere
  useEffect(() => {
    const parsed = parseThemeCookieValue(themeCookie);
    setSelectedThemeId(parsed);
  }, [themeCookie]);

  const createChartRecordMutation = useCreateChartRecordMutation({
    onSuccess: response => {
      chartRecordIdRef.current = response.record.id;
      chartRecordedRef.current = true;
    },
    onError: error => {
      console.warn('Failed to record initial chart creation:', error);
    },
  });

  const saveChartPreferences = useCallback(() => {
    if (chartRecordedRef.current || !chartType || createChartRecordMutation.isPending) {
      return;
    }

    // Map the chart type to our backend enum
    const mappedChartType = mapChartTypeToBackend(chartType);
    if (!mappedChartType) {
      return;
    }

    createChartRecordMutation.mutateAsync({
      initialChartType: mappedChartType,
    });
  }, [chartType, createChartRecordMutation]);

  // Record initial chart creation
  useEffect(() => {
    saveChartPreferences();
  }, [saveChartPreferences]);

  return (
    <ChartLegendContainer>
      <ChartInfoContext.Provider value={[chart, setChart]}>
        <ChartGroupByContext.Provider value={[groupBy, setGroupBy]}>
          <ChartTypeContext.Provider value={[chartType, setChartType]}>
            <ChartViewContext.Provider value={ChartView}>
              <ChartRecordIdContext.Provider value={chartRecordIdRef.current}>
                <ChartThemeIdContext.Provider value={[selectedThemeId, setSelectedThemeId]}>
                  <CurrentSegmentIdsContext.Provider
                    value={{ projectId: props.chart?.projectId ?? null, segmentId: null, projectParentId: null, slug: null }}>
                    {props.children}
                  </CurrentSegmentIdsContext.Provider>
                </ChartThemeIdContext.Provider>
              </ChartRecordIdContext.Provider>
            </ChartViewContext.Provider>
          </ChartTypeContext.Provider>
        </ChartGroupByContext.Provider>
      </ChartInfoContext.Provider>
    </ChartLegendContainer>
  );
};